import useWindowDimensions from '@/components/Hooks/WindowDimension';
import { GetBookingTimesQuery, Specialty } from '@/graphql/graphql';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectHomepage, setBookingTime, setOpenModal } from '@/redux/state/homepage';
import { selectLanguage } from '@/redux/state/language';
import moment from 'moment';
import 'moment/locale/ar';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, CardBody, CardFooter, CardTitle, Col, Container, Row } from 'reactstrap';
import { MappedAvailableTimesQuery } from './BookingTimeCard';
import GenericCard from './GenericCard';

const pickService_t_prefix = "pages.homePage.pickService";
const homepage_t_prefix = "pages.homePage";

type BookingTime = {
    booked: boolean;
    timeSlot: { utcDate: string };
    contactType: { name: string };
    lawyer: {
        id: string;
        en: { name: string };
        ar: { name: string };
        specialties: Array<{
            ar: { title: string; description?: string | null };
            en: { title: string; description?: string | null };
        }>;
    };
};

interface ListingsProps {
    validTimes: BookingTime[]
}

const Listings: React.FC<ListingsProps> = ({ validTimes }) => {
    const { t } = useTranslation();
    const style: React.CSSProperties = {
        padding: 0
    }
    return (
        <Container className="available-listing" style={style}>
            <Badge className="mr-1">
                {t(`${pickService_t_prefix}.available`)} {validTimes.length}
            </Badge>
            {validTimes.map((bookingTime, index) => (
                <BookingTimeSlot key={index} bookingTime={bookingTime} />
            ))}
        </Container>
    );
};

interface BookingTimeSlotProps {
    bookingTime: BookingTime
}
const BookingTimeSlot: React.FC<BookingTimeSlotProps> = ({ bookingTime }) => {
    const { t } = useTranslation();
    const { width } = useWindowDimensions()
    const select = t(`${pickService_t_prefix}.select`);
    const profile = t(`${pickService_t_prefix}.profile`);
    const { currentLanguage } = useAppSelector(selectLanguage)
    const {
        timeSlot: { utcDate },
    } = bookingTime;
    const styles: React.CSSProperties = {
        margin: "0.5rem 0",
        textAlign: "center",
        color: "#343a40",
        backgroundColor: "rgb(255 255 255 / 48%)",
    };
    const lawyerName: string = bookingTime.lawyer[currentLanguage].name
    const imgColStyleMobile: React.CSSProperties = {
        padding: currentLanguage === "ar" ? "0 1rem 0 0" : "0 1rem 0 0"
    }
    const imgColStyleDesktop: React.CSSProperties = {
        padding: currentLanguage === "ar" ? "0 0 0 1rem" : "0 1rem 0 0"
    }
    const imgColStyle = width < 993 ? imgColStyleMobile : imgColStyleDesktop
    const date = moment(utcDate).locale(currentLanguage).format("dddd - hA");
    const { choices } = useAppSelector(selectHomepage);
    const isChosen = () => choices.bookingTime === bookingTime.timeSlot.utcDate;
    const dispatch = useAppDispatch();
    const [isProfileTapped, setIsProfileTapped] = useState(false);
    const [isTapped, setIsTapped] = useState(false);
    const handleClick = (e: any) => {
        e.preventDefault();
        dispatch(setBookingTime({ utcBookingTime: bookingTime.timeSlot.utcDate, chosenLawyer: bookingTime.lawyer }));
        dispatch(setOpenModal({ modal: "bookingTime", isOpen: false }));
    };
    return (
        <GenericCard styles={styles}>
            <h4
                style={{ margin: 0, textTransform: "capitalize" }}
                className="category-social"
            >
                <Container>
                    <Row style={{ margin: "initial" }}>
                        <Col style={imgColStyle} className="listing-img-col">
                            <img
                                alt="..."
                                className="img"
                                src={require("assets/img/james.jpg")}
                            ></img>
                        </Col>
                        <Col>
                            <Row className="lawyer-name" style={{ marginTop: "0.5rem" }}>
                                <h6>{lawyerName}</h6>
                            </Row>
                            <Row className="lawyer-date" style={{ marginTop: "1rem" }}>
                                <h6>{date}</h6>
                            </Row>
                            <Row className="lawyer-price" style={{ marginTop: "0" }}>
                                <h6>150 جنيه</h6>
                            </Row>
                        </Col>
                        <Col>
                            <Row>
                                <Button
                                    className={`select-button ${isChosen() ? "isChosen" : isProfileTapped ? "isTapped" : ""
                                        }`}
                                    onClick={handleClick}
                                    onMouseEnter={() => setIsProfileTapped(true)}
                                    onMouseLeave={() => setIsProfileTapped(false)}
                                    style={{ fontSize: "initial" }}
                                >
                                    {profile}
                                </Button>
                            </Row>
                            <Row>
                                <Button
                                    className={`select-button ${isChosen() ? "isChosen" : isTapped ? "isTapped" : ""
                                        }`}
                                    onClick={handleClick}
                                    onMouseEnter={() => setIsTapped(true)}
                                    onMouseLeave={() => setIsTapped(false)}
                                >
                                    {select}
                                </Button>
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </h4>
        </GenericCard>
    );
};

export const ExampleCard: React.FC = () => {
    const { t } = useTranslation();
    const { choices: { clientReservation } } = useAppSelector(selectHomepage)
    const { currentLanguage } = useAppSelector(selectLanguage)
    const pounds =  currentLanguage === 'en' ? "L.E." : "جنيه"
    if (!clientReservation) {
        return <></>
    }
    const lawyerSpecialties = clientReservation.assignedLawyer.specialties
    return (
        <Card className="card-profile">
            <Row>
                <Col style={{padding: 0}}>
                    <div className="card-image">
                        <a href="#pablo" onClick={(e) => e.preventDefault()}>
                            <img
                                alt="..."
                                className="img"
                                src={require("assets/img/james.jpg")}
                            ></img>
                        </a>
                    </div>
                </Col>
                <Col>
                    <CardBody>
                        <CardTitle style={{ color: "initial" }} tag="h3">{clientReservation.assignedLawyer[currentLanguage].name}</CardTitle>
                        <h6 className="category text-info">
                            {lawyerSpecialties.map((specialty, index) => (`${specialty[currentLanguage].title} - `))}
                        </h6>
                        <p className="card-description">
                            150 {pounds}
                        </p>
                        <Button
                            className={`select-button isChosen checkout`}
                            block={true}
                        // className={`select-button ${isChosen() ? "isChosen" : isTapped ? "isTapped" : ""}`}
                        // onClick={handleClick}
                        // onMouseEnter={() => setIsTapped(true)}
                        // onMouseLeave={() => setIsTapped(false)}
                        >
                            {t(`${homepage_t_prefix}.checkout`)}
                        </Button>
                    </CardBody>
                </Col>
            </Row>
        </Card>
    )
}

export default Listings;